/* Was ist AYU? – Sacred Modern Design
 * Light section with 3-step process
 * Clean, warm ivory background
 */
import { STEPS } from "@/lib/constants";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { ClipboardList, Dna, Sparkles } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";

const stepIcons = [ClipboardList, Dna, Sparkles];

export default function WasIstAyuSection() {
  const { t } = useI18n();
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section id="was-ist-ayu" className="section-light py-24 lg:py-32 relative" ref={ref}>
      {/* Gold line divider */}
      <div className="gold-line w-full absolute top-0" />

      <div className="container">
        {/* Section Header */}
        <div
          className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <span className="text-sm font-semibold tracking-[0.2em] uppercase text-[oklch(0.52_0.09_128)]" style={{ fontFamily: "var(--font-body)" }}>
            {t("landing.wasIstAyu.badge")}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[oklch(0.25_0.04_50)] mt-4 mb-6">
            {t("landing.wasIstAyu.heading")}
          </h2>
          <p className="text-lg text-[oklch(0.45_0.04_50)] leading-relaxed" style={{ fontFamily: "var(--font-body)" }}>
            {t("landing.wasIstAyu.description")}
          </p>
        </div>

        {/* 3 Steps */}
        <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
          {STEPS.map((step, i) => {
            const Icon = stepIcons[i];
            return (
              <div
                key={i}
                className={`relative group transition-all duration-700 ${
                  isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                }`}
                style={{ transitionDelay: `${i * 150 + 200}ms` }}
              >
                {/* Connector line */}
                {i < STEPS.length - 1 && (
                  <div className="hidden md:block absolute top-12 left-[60%] w-[80%] h-px bg-gradient-to-r from-[oklch(0.52_0.09_128/0.3)] to-transparent" />
                )}

                <div className="bg-white rounded-2xl p-8 shadow-sm border border-[oklch(0.88_0.02_80)] hover:shadow-lg hover:border-[oklch(0.52_0.09_128/0.3)] transition-all duration-300 h-full">
                  {/* Step number */}
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 rounded-xl bg-[oklch(0.52_0.09_128/0.1)] flex items-center justify-center group-hover:bg-[oklch(0.52_0.09_128/0.2)] transition-colors">
                      <Icon className="text-[oklch(0.52_0.09_128)]" size={24} />
                    </div>
                    <span className="text-4xl font-bold text-[oklch(0.88_0.02_80)]" style={{ fontFamily: "var(--font-display)" }}>
                      {step.number}
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-[oklch(0.25_0.04_50)] mb-3">
                    {t(step.titleKey as any)}
                  </h3>
                  <p className="text-[oklch(0.5_0.03_50)] leading-relaxed" style={{ fontFamily: "var(--font-body)" }}>
                    {t(step.descriptionKey as any)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
